import { useEffect, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { useSession } from '@repo/api-client/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AiChat } from './AiChat';
import { useAiComposer } from './AiComposerContext';

/**
 * Persistent AI presence anchored to the bottom-right corner. Two
 * shapes:
 *
 *   • **Idle** — a soft, breathing orb with a sparkles glyph. A
 *     thought-bubble periodically surfaces ONE of a rotating set of
 *     gentle nudges ("Want me to draft an invoice for Acme?") so
 *     non-tech users discover what the assistant can do without
 *     having to read documentation. The rotation crossfades (no
 *     typewriter — typewriter on a big web canvas reads as nervous).
 *
 *   • **Open** — a substantial chat panel grows out from the corner.
 *     It is NOT a centered modal and there is NO backdrop. The page
 *     stays fully interactive so the user can keep clicking around
 *     while the chat is open. The panel is large enough to host a
 *     real conversation (PlanCard, ReminderCard, etc. all fit).
 *
 * Dismiss: explicit close button (X). Esc closes too. Click-outside
 * intentionally does NOT close — Intercom-style widgets dismiss too
 * eagerly when the user is referencing the page.
 */

const HINTS: ReadonlyArray<string> = [
  'Want to draft an invoice for Acme?',
  'I can send those overdue reminders.',
  'Curious about this month?',
  'Need a polite check-in for Globex?',
  'Want a thank-you note for Liam?',
  'Ask me anything.',
];

const HINT_VISIBLE_MS = 4200;
const HINT_FADE_MS = 320;
const MAX_HINT_ROTATIONS = 6; // stop nudging after this — never annoying

/**
 * Crossfade rotation through HINTS. Returns the current hint plus a
 * `visible` flag so the consumer can drive a fade transition. Stops
 * after MAX_HINT_ROTATIONS so the assistant doesn't keep pestering
 * a user who's choosing not to engage.
 */
function useRotatingHint(
  enabled: boolean,
): { hint: string; visible: boolean; index: number } {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [rotations, setRotations] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setVisible(false);
      return;
    }
    if (rotations >= MAX_HINT_ROTATIONS) {
      setVisible(false);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    function fadeIn() {
      if (cancelled) return;
      setVisible(true);
      timer = setTimeout(fadeOut, HINT_VISIBLE_MS);
    }

    function fadeOut() {
      if (cancelled) return;
      setVisible(false);
      timer = setTimeout(advance, HINT_FADE_MS + 200);
    }

    function advance() {
      if (cancelled) return;
      setIndex((i) => (i + 1) % HINTS.length);
      setRotations((r) => r + 1);
    }

    // First appearance is delayed so the orb has a moment to settle
    // before talking at the user.
    timer = setTimeout(fadeIn, 1400);

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [enabled, index, rotations]);

  return {
    hint: HINTS[index] ?? HINTS[0]!,
    visible,
    index,
  };
}

export function AiAssistantOrb() {
  const { isOpen, open, close, seededPrompt, seededConversationId } =
    useAiComposer();
  const { user } = useSession();
  const { hint, visible: hintVisible } = useRotatingHint(!isOpen);

  return (
    <>
      {/* IDLE — orb + rotating thought bubble. We keep them mounted
          but slide them out (translate + fade) when the panel opens
          so the transition feels like the orb is morphing into the
          panel rather than the panel popping in from nothing. */}
      <div
        className={cn(
          'pointer-events-none fixed bottom-6 right-6 z-30 flex flex-col items-end gap-3 transition-all duration-300',
          isOpen ? 'pointer-events-none translate-y-2 opacity-0' : 'opacity-100',
        )}
        aria-hidden={isOpen}
      >
        {/* Thought bubble */}
        <div
          className={cn(
            'pointer-events-auto max-w-[280px] rounded-2xl rounded-br-sm border border-line bg-card px-3.5 py-2.5 text-sm leading-snug shadow-lg shadow-black/10 transition-all duration-300',
            hintVisible
              ? 'translate-y-0 opacity-100'
              : 'pointer-events-none translate-y-2 opacity-0',
          )}
          // The bubble is also a click target — non-tech users will
          // try clicking the words, not just the orb.
          role="button"
          tabIndex={hintVisible ? 0 : -1}
          onClick={() => open({ prompt: hint })}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              open({ prompt: hint });
            }
          }}
        >
          <span className="text-foreground">{hint}</span>
        </div>

        {/* Orb */}
        <button
          type="button"
          onClick={() => open()}
          aria-label="Open the AI assistant"
          className="pointer-events-auto group relative flex size-14 items-center justify-center"
        >
          {/* Soft glow ring — sits behind the orb and pulses gently */}
          <span
            aria-hidden
            className="absolute inset-0 rounded-full bg-accent/30 blur-md transition-all duration-500 group-hover:bg-accent/50 group-hover:blur-lg"
          />
          <span
            aria-hidden
            className="absolute inset-0 animate-ping rounded-full bg-accent/15"
            style={{ animationDuration: '3s' }}
          />
          {/* Orb body — premium gradient, not flat */}
          <span className="relative flex size-14 items-center justify-center rounded-full bg-gradient-to-br from-accent via-accent to-accent/80 text-accent-foreground shadow-lg shadow-accent/30 transition-transform duration-200 group-hover:scale-[1.06]">
            <Sparkles className="size-5" />
          </span>
        </button>
      </div>

      {/* OPEN — chat panel. Bottom-right anchored, no backdrop, page
          stays interactive. Sized large enough to feel like a real
          surface, not a popover. */}
      <div
        className={cn(
          'fixed bottom-6 right-6 z-40 flex origin-bottom-right flex-col overflow-hidden rounded-3xl border border-line bg-card shadow-2xl shadow-black/25 transition-all duration-300',
          // Width is bounded so it's substantial but never edge-to-edge
          // on a small viewport. Height likewise leaves breathing room
          // for the top bar so the chat header reads as a peer of the
          // page nav.
          'h-[min(740px,calc(100vh-96px))] w-[min(460px,calc(100vw-32px))]',
          isOpen
            ? 'pointer-events-auto scale-100 opacity-100'
            : 'pointer-events-none scale-90 opacity-0',
        )}
        role="dialog"
        aria-label="AI assistant"
        aria-hidden={!isOpen}
        // Trap Escape inside the panel so closing the chat doesn't
        // collide with other Escape handlers on the page.
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            close();
          }
        }}
      >
        {/* Greeting strip on top — gives the panel an identity above
            the bare-bones chat header. The greeting is firstName when
            we have one, falls back to a generic salutation otherwise.
            Non-tech users respond to "Hi <name>" — it feels personal,
            not algorithmic. */}
        <div className="flex items-center gap-3 border-b border-line bg-gradient-to-br from-accent-soft/70 via-accent-soft/30 to-card px-4 py-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-accent to-accent/80 text-accent-foreground shadow-sm">
            <Sparkles className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">
              Hi {user?.firstName?.split(' ')[0] ?? 'there'} 👋
            </p>
            <p className="truncate text-[11px] text-muted-foreground">
              I can draft invoices, send reminders, or summarise your month.
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Close AI assistant"
            onClick={close}
            className="h-8 w-8 rounded-full"
          >
            <X className="size-4" />
          </Button>
        </div>

        <div className="min-h-0 flex-1">
          <AiChat
            active={isOpen}
            seededPrompt={seededPrompt}
            seededConversationId={seededConversationId}
            hideHeader
          />
        </div>
      </div>
    </>
  );
}
